import { Skeleton } from '@/shared/components/ui/skeleton';
import { useCareScoutWidget } from '../hooks/useCareScoutWidget';
import type { CareScoutWidgetCallbacks } from '../types/careCosts.types';
import { WidgetFallback } from './WidgetFallback';

const WIDGET_URL_FALLBACK = 'https://www.carescout.com/cost-of-care/widget?embed=1&lang=en';

type CareScoutWidgetProps = CareScoutWidgetCallbacks;

export const CareScoutWidget = ({ onLoaded, onError }: CareScoutWidgetProps = {}) => {
  // D-15, WIDGET-10: read inside render function (not module top-level) so test env overrides work
  const widgetUrl = import.meta.env.VITE_CARESCOUT_WIDGET_URL || WIDGET_URL_FALLBACK;

  const { iframeRef, iframeHeight, widgetStatus, onIframeLoad, onIframeError } = useCareScoutWidget({
    onLoaded,
    onError,
  });

  // care_cost_page_view is NOT fired here: this widget is reused on the FP/SP
  // dashboards, and emitting a public-page-view event on a dashboard tab would
  // pollute the metric. The owning public page (CareCostsPage) fires it instead.

  // Early return: if widgetUrl is empty/falsy, render fallback immediately (UI-SPEC: Missing Env Var)
  if (!widgetUrl) {
    return <WidgetFallback />;
  }

  // Error state: timeout fired before onLoad (WIDGET-04, D-03)
  if (widgetStatus === 'error') {
    return <WidgetFallback />;
  }

  return (
    <div className="w-full min-w-[393px]">
      {/* Loading state: Skeleton visible, iframe hidden in DOM so onLoad fires (WIDGET-03) */}
      {/* D-02 override: display:none per UI-SPEC (not visibility:hidden per D-02) */}
      {widgetStatus === 'loading' && (
        <Skeleton className="w-full" style={{ height: `${iframeHeight}px` }} />
      )}
      <iframe
        ref={iframeRef}
        src={widgetUrl}
        title="Cost of Care Tool - CareScout"
        width="100%"
        style={{
          height: `${iframeHeight}px`,
          border: 'none',
          // WIDGET-03 + UI-SPEC: display:none during loading keeps iframe in DOM so onLoad fires
          // D-02 override: using display:none (not visibility:hidden) — both fire onLoad in modern browsers
          display: widgetStatus === 'loading' ? 'none' : 'block',
        }}
        onLoad={onIframeLoad}
        onError={onIframeError}
        referrerPolicy="origin-when-cross-origin"
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
      />
    </div>
  );
};
